import React, { useState } from 'react';
import { Users, Eye, EyeOff, Share2, Download, Copy, QrCode } from 'lucide-react';

const ShareWithNanny = ({ routine, dailyLog, isCaregiverMode, setIsCaregiverMode, categories }) => {
  const [shareLink, setShareLink] = useState('');
  const [showQR, setShowQR] = useState(false);

  const generateShareLink = () => {
    const data = {
      routine,
      dailyLog,
      timestamp: new Date().toISOString()
    };
    
    // In a real app, this would be a server-generated link
    const mockLink = `https://babyday.app/share/${btoa(JSON.stringify(data))}`;
    setShareLink(mockLink);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const exportSummary = () => {
    const completedEvents = routine.filter(event => dailyLog[event.id]?.completed);
    const totalEvents = routine.length;
    const completionRate = totalEvents > 0 ? (completedEvents.length / totalEvents) * 100 : 0;

    const summary = `
BabyDay Daily Summary
${new Date().toLocaleDateString('en-US', { 
  weekday: 'long', 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
})}

📊 Statistics:
• Events Completed: ${completedEvents.length}/${totalEvents}
• Completion Rate: ${Math.round(completionRate)}%

📝 Completed Events:
${completedEvents.map(event => {
  const log = dailyLog[event.id];
  const category = categories[event.category];
  return `• ${category.icon} ${event.title} (${log.actualTime})`;
}).join('\n')}

📋 Notes:
${Object.entries(dailyLog).map(([eventId, log]) => {
  const event = routine.find(e => e.id === eventId);
  if (log.notes) {
    return `• ${event.title}: ${log.notes}`;
  }
  return null;
}).filter(Boolean).join('\n')}
    `.trim();

    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `babyday-summary-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2>Share & Collaborate</h2>
        <button
          className={`btn ${isCaregiverMode ? 'btn-secondary' : ''}`}
          onClick={() => setIsCaregiverMode(!isCaregiverMode)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {isCaregiverMode ? <EyeOff size={20} /> : <Eye size={20} />}
          {isCaregiverMode ? 'Exit Caregiver Mode' : 'Enter Caregiver Mode'}
        </button>
      </div>

      {isCaregiverMode && (
        <div className="card" style={{ marginBottom: '24px', border: '2px solid #667eea' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Users size={20} color="#667eea" />
            <h3 style={{ color: '#667eea', margin: 0 }}>Caregiver Mode Active</h3>
          </div>
          <p style={{ color: '#6c757d', margin: 0 }}>
            You can view the routine and log events, but cannot edit the routine schedule.
          </p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        <div className="card">
          <h3>Share Routine</h3>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>
            Generate a shareable link for caregivers to view and log events.
          </p>
          
          <div style={{ marginBottom: '16px' }}>
            <button className="btn" onClick={generateShareLink} style={{ marginBottom: '12px' }}>
              <Share2 size={16} style={{ marginRight: '8px' }} />
              Generate Share Link
            </button>
          </div>

          {shareLink && (
            <div>
              <div className="form-group">
                <label>Share Link</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="form-control"
                    value={shareLink}
                    readOnly
                  />
                  <button className="btn btn-secondary" onClick={copyToClipboard}>
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowQR(!showQR)}
                style={{ marginTop: '12px' }}
              >
                <QrCode size={16} style={{ marginRight: '8px' }} />
                {showQR ? 'Hide' : 'Show'} QR Code
              </button>
              
              {showQR && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '16px', 
                  background: '#f8f9fa', 
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    width: '200px', 
                    height: '200px', 
                    background: '#e9ecef', 
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: '#6c757d' }}>QR Code Placeholder</span>
                  </div>
                  <p style={{ marginTop: '8px', fontSize: '14px', color: '#6c757d' }}>
                    Scan to access the routine
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card">
          <h3>Export Summary</h3>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>
            Export today's summary as a text file to share with family or save for records.
          </p>
          
          <button className="btn" onClick={exportSummary}>
            <Download size={16} style={{ marginRight: '8px' }} />
            Export Daily Summary
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: '24px' }}>
        <h3>Caregiver Instructions</h3>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <h4>For Moms:</h4>
            <ul style={{ color: '#6c757d', lineHeight: '1.6' }}>
              <li>Use "Enter Caregiver Mode" to give limited access to nannies</li>
              <li>Generate share links for easy access</li>
              <li>Export summaries to keep family updated</li>
              <li>Review logs to understand how the day went</li>
            </ul>
          </div>
          
          <div>
            <h4>For Caregivers:</h4>
            <ul style={{ color: '#6c757d', lineHeight: '1.6' }}>
              <li>View the routine schedule to know what's planned</li>
              <li>Log actual times when events happen</li>
              <li>Add notes about how the baby responded</li>
              <li>Mark events as completed when done</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Quick Stats</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#667eea' }}>
              {routine.length}
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>Total Events</div>
          </div>
          
          <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#28a745' }}>
              {Object.keys(dailyLog).length}
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>Events Logged</div>
          </div>
          
          <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#ffc107' }}>
              {Object.values(dailyLog).filter(log => log.completed).length}
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>Completed</div>
          </div>
          
          <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc3545' }}>
              {Object.values(dailyLog).filter(log => !log.completed).length}
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>Incomplete</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareWithNanny; 